// Upload size/shape limits and their user-facing messages.
//
// Pure and dependency-free on purpose: `lib/api/uploads.ts` re-exports from
// here so callers keep importing the limit from `@/lib/api`, but the rules
// themselves stay behind no axios/firebase import and can be unit-tested
// directly (same split as `upload-mount-path.ts`). One definition of the cap,
// used by every attach surface.

// Cloud Run / the Google Front End reject request bodies over 32 MiB with a
// 413 that carries no CORS headers — so cross-origin the browser can't read
// the status and surfaces an opaque "Failed to fetch".
//
// Since ENG-1820 this is a ROUTING threshold, not the user-facing cap: a file
// over it is staged straight into GCS by the direct-upload path instead of
// being refused, so it never becomes a request body and never meets the 413.
// The cap the user actually hits is MAX_DIRECT_UPLOAD_BYTES below.
export const MAX_UPLOAD_BYTES = 32 * 1024 * 1024;
// Reserve only a sliver for multipart framing (boundaries + the tiny
// tags/project_id/scope fields are a few hundred bytes), so files right up to
// the advertised 32 MB cap still go through as a multipart body.
export const MULTIPART_SIZE_LIMIT = MAX_UPLOAD_BYTES - 64 * 1024;

// The real ceiling, enforced server-side by `upload_direct.MAX_DIRECT_UPLOAD_BYTES`.
// Bounded by how much a Cloud Run instance can materialize while pushing the
// file to the Anthropic Files API, not by anything on this side — a Python test
// asserts the two agree, because a drift strands files in the gap between "too
// big to POST" and "too small to stage".
export const MAX_DIRECT_UPLOAD_BYTES = 200 * 1024 * 1024;

/** A user-facing "too large" message, or null when the file is within limits. */
export function uploadTooLargeMessage(file: File): string | null {
  if (file.size <= MAX_DIRECT_UPLOAD_BYTES) return null;
  const mb = (file.size / (1024 * 1024)).toFixed(1);
  const limitMb = MAX_DIRECT_UPLOAD_BYTES / (1024 * 1024);
  return `"${file.name}" is ${mb} MB — over the ${limitMb} MB upload limit.`;
}

/**
 * Pre-flight reasons a file can't be attached to a composer at all, checked
 * before any network call so the user gets a precise message instead of a
 * failure the browser can only describe opaquely.
 *
 * Oversize matters most. It used to mean anything over 32 MB: such a body died
 * on the CORS-less 413 above and reached the app as a bare `TypeError: Failed
 * to fetch` (ENG-3458 — a 50 MB deck). Since ENG-1820 that file uploads instead
 * — the guard now fires only past the real ceiling — but the rule still lives
 * here so every attach surface inherits exactly one definition of it.
 *
 * Returns a user-facing message, or null when the file is attachable.
 */
export function attachRejectionMessage(file: File): string | null {
  // Dropping a folder yields a zero-byte entry — the upload would "succeed"
  // and mount an empty Source, so reject it with the real explanation.
  if (file.size === 0) {
    return `Can't attach "${file.name}": file is empty (folders aren't supported).`;
  }
  return uploadTooLargeMessage(file);
}

/**
 * Whether this file is routed to the direct-to-GCS upload path.
 *
 * The single definition of the routing rule `uploadFileWithProgress` applies.
 * Anything that needs to know which path a file took — retry idempotency,
 * analytics labelling — asks this instead of re-deriving `> MULTIPART_SIZE_LIMIT`,
 * so moving the threshold moves every consumer with it. Two call sites had
 * already copied the comparison before this existed, where a threshold change
 * would have silently mislabelled every upload_failed event with no test
 * failing.
 */
export function usesDirectUpload(file: { size: number }): boolean {
  return file.size > MULTIPART_SIZE_LIMIT;
}

/** Why `attachRejectionMessage` refused a file, as a stable analytics reason.
 *
 * Classified from the same conditions the message is built from, so a caller
 * reporting a pre-flight rejection never has to parse the user-facing copy —
 * the same rule `describeUploadFailure` follows for post-flight failures. */
export function attachRejectionReason(
  file: { size: number },
): "empty_file" | "too_large" | null {
  if (file.size === 0) return "empty_file";
  if (file.size > MAX_DIRECT_UPLOAD_BYTES) return "too_large";
  return null;
}

/**
 * The message for an upload that failed with no HTTP response, before the
 * request body had finished sending.
 *
 * This is the multipart path's `!e.response && !bodySent` branch. It used to
 * read "the connection dropped or the file is too large" — and the size half
 * of that is **unreachable at that call site**: `uploadFileWithProgress`
 * routes anything over `MULTIPART_SIZE_LIMIT` to the direct-to-GCS path and
 * returns before it, so a file that reaches this branch is by construction
 * within the multipart cap. The clause was left over from before ENG-1820,
 * when >32 MiB really did die here on a CORS-less 413.
 *
 * It was not harmless. ENG-5238: a prospect attached four ~200 KB files, hit
 * this branch, was told his files might be too large, and concluded he was
 * being paywalled ("oh, they're gating me, they want me to pay"). Never offer
 * size as an explanation for a failure size cannot cause.
 *
 * **Claim only what this branch actually establishes.** `bodySent` flips at
 * `loaded >= total`, so reaching here proves the body did not *finish* — not
 * that none of it left the machine. A reset at 60% lands here too, and an
 * earlier draft of this copy told that user "the file never left your browser,
 * so nothing was sent", which is the same unsupported-cause mistake in a new
 * costume. What holds either way is that the upload did not complete and the
 * server stored nothing, which is also why a retry is safe.
 *
 * So the copy leads with Retry — correct for the transient drop, and what the
 * Sources modal's button actually does — and offers re-selecting as the
 * fallback, which is the remedy for the other plausible cause (a `File` handle
 * gone stale: a cloud-sync placeholder that never hydrated, or a file moved
 * since the picker handed it over). Retrying a stale handle just re-reads the
 * dead handle, so the two must be ordered, not conflated.
 */
export function transferFailureMessage(
  file: { name: string },
  opts: { online?: boolean } = {},
): string {
  if (opts.online === false) {
    return `Couldn't upload "${file.name}" — you appear to be offline. Reconnect and try again.`;
  }
  return (
    `Couldn't upload "${file.name}" — the upload didn't finish, so nothing was saved. ` +
    `Try again; if it keeps failing, re-select the file ` +
    `(one synced from OneDrive, Dropbox or iCloud may need downloading first).`
  );
}
